import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronDown, ChevronRight, ToggleLeft, ToggleRight, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RMRequirement() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [customerId, setCustomerId] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [showNet, setShowNet] = useState(false);
  const { exporting, handleExport } = usePdfExport('/export/rm-requirement-pdf', (params) => `RM_Requirement_${params?.detailed === 'true' ? 'Detailed' : 'Summary'}`);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/masters/customers`, {});
      setCustomers(res.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchRequirement = async () => {
    if (!month) { toast.error('Select a month'); return; }
    setLoading(true);
    try {
      let url = `${API}/reports/rm-requirement?month=${month}`;
      if (customerId && customerId !== 'all') url += `&customer_id=${encodeURIComponent(customerId)}`;
      const response = await axios.get(url, {});
      setData(response.data);
      if (response.data.length === 0) toast.info('No schedules found for the selected filters');
    } catch (err) { console.error(err); toast.error('Failed to fetch RM requirement'); }
    finally { setLoading(false); }
  };

  const toggleRow = (idx) => setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));

  const totalPieces = data.reduce((s, d) => s + d.total_pieces, 0);
  const totalWeight = data.reduce((s, d) => s + d.total_weight, 0);
  const totalAvailable = data.reduce((s, d) => s + (d.total_available || 0), 0);
  const totalNet = data.reduce((s, d) => s + (d.net_weight || 0), 0);
  const totalPendingPieces = data.reduce((s, d) => s + (d.pending_pieces || 0), 0);
  const totalPendingWeight = data.reduce((s, d) => s + (d.pending_weight || 0), 0);

  const exportParams = { month };
  if (customerId !== 'all') exportParams.customer_id = customerId;

  return (
    <div className="space-y-6" data-testid="rm-requirement-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="rm-requirement-title">RM REQUIREMENT</h1>
          <p className="text-sm text-text-muted mt-2">Calculate raw material requirement based on production schedule</p>
        </div>
        {data.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-pdf-button" disabled={exporting} className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium">
                <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'EXPORT PDF'}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="export-summary-option" onClick={() => handleExport(exportParams)}>
                <div>
                  <div className="font-semibold text-sm">Summary</div>
                  <div className="text-[11px] text-text-muted">One row per material (grade + section)</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-detailed-option" onClick={() => handleExport({ ...exportParams, detailed: 'true' })}>
                <div>
                  <div className="font-semibold text-sm">Detailed (Expanded)</div>
                  <div className="text-[11px] text-text-muted">Includes per-part rows, stage stock & vendor WIP</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="bg-white border border-border rounded-sm p-6">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Month</label>
            <Input data-testid="month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm w-48" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Customer</label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="customer-filter" className="w-56 rounded-sm">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="calculate-button" onClick={fetchRequirement} disabled={loading} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium">
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'CALCULATING...' : 'CALCULATE'}
          </Button>
          {data.length > 0 && (
            <Button data-testid="toggle-net-button" variant={showNet ? "default" : "outline"} onClick={() => setShowNet(!showNet)} className="rounded-sm font-medium ml-auto">
              {showNet ? <ToggleRight className="w-4 h-4 mr-2" /> : <ToggleLeft className="w-4 h-4 mr-2" />}
              {showNet ? 'NET REQUIREMENT' : 'TOTAL REQUIREMENT'}
            </Button>
          )}
        </div>
      </div>

      {data.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white border border-border rounded-sm p-4" data-testid="total-pieces-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Total Scheduled Pcs</p>
            <p className="text-2xl font-bold font-mono mt-1">{totalPieces.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="pending-pieces-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Pending Schedule Pcs</p>
            <p className="text-2xl font-bold font-mono mt-1 text-amber-600">{totalPendingPieces.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="total-weight-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Total RM Required</p>
            <p className="text-2xl font-bold font-mono mt-1 text-accent">{totalWeight.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="total-available-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Available Stock (Wt)</p>
            <p className="text-2xl font-bold font-mono mt-1 text-green-600">{totalAvailable.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="net-weight-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Net RM Required</p>
            <p className={`text-2xl font-bold font-mono mt-1 ${totalNet > 0 ? 'text-red-600' : 'text-green-600'}`}>{totalNet.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
        </div>
      )}

      {data.length > 0 && (
        <div className="bg-white border border-border rounded-sm">
          <div className="p-4 border-b border-border">
            <h2 className="text-sm font-semibold uppercase tracking-wider">
              {showNet ? 'Net Requirement by Material (After Stock Deduction)' : 'Total Requirement by Material (Schedule Based)'}
              {customerId !== 'all' && <span className="text-accent ml-2">-- {customers.find(c => c.customer_id === customerId)?.customer_name}</span>}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm" style={{tableLayout: 'fixed'}}>
              <colgroup>
                <col style={{width: '40px'}} />
                <col />
                <col />
                <col style={{width: '100px'}} />
                <col style={{width: '100px'}} />
                <col style={{width: '120px'}} />
                {showNet && <>
                  <col style={{width: '110px'}} />
                  <col style={{width: '110px'}} />
                  <col style={{width: '110px'}} />
                  <col style={{width: '120px'}} />
                </>}
              </colgroup>
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider"></th>
                  <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Steel Grade</th>
                  <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                  <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Total Pcs</th>
                  <th className="h-10 px-3 text-right align-middle font-medium text-amber-600 bg-amber-50/50 uppercase text-xs tracking-wider">Pending Pcs</th>
                  <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Total Required</th>
                  {showNet && (
                    <>
                      <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">RM Stock</th>
                      <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage Stock</th>
                      <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor WIP</th>
                      <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider font-bold">Net Required</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <>
                    <tr key={`row-${idx}`} className="border-b transition-colors hover:bg-muted/50 cursor-pointer" onClick={() => toggleRow(idx)} data-testid={`req-row-${idx}`}>
                      <td className="px-3 py-3 align-middle">
                        {row.parts.length > 0 && (expandedRows[idx] ? <ChevronDown className="w-4 h-4 text-text-muted" /> : <ChevronRight className="w-4 h-4 text-text-muted" />)}
                      </td>
                      <td className="px-3 py-3 align-middle font-mono text-xs font-semibold">{row.steel_grade}</td>
                      <td className="px-3 py-3 align-middle font-mono text-xs">{row.section}</td>
                      <td className="px-3 py-3 align-middle text-right font-mono text-xs">{row.total_pieces.toLocaleString()}</td>
                      <td className="px-3 py-3 align-middle text-right font-mono text-xs font-semibold text-amber-600">{(row.pending_pieces || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 align-middle text-right font-mono text-xs font-semibold text-accent">{row.total_weight.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      {showNet && (
                        <>
                          <td className="px-3 py-3 align-middle text-right font-mono text-xs text-green-600">{(row.available_rm_stock || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className="px-3 py-3 align-middle text-right font-mono text-xs text-green-600">{(row.available_stage_stock || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className="px-3 py-3 align-middle text-right font-mono text-xs text-green-600">{(row.available_vendor_wip || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className={`px-3 py-3 align-middle text-right font-mono text-xs font-bold ${(row.net_weight || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {(row.net_weight || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                          </td>
                        </>
                      )}
                    </tr>
                    {expandedRows[idx] && row.parts.map((part, pidx) => (
                      <tr key={`part-${idx}-${pidx}`} className="bg-gray-50/50 border-b" data-testid={`req-part-${idx}-${pidx}`}>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 align-middle text-xs text-text-muted pl-4" colSpan={showNet ? 1 : 2}>
                          <span className="font-mono font-medium">{part.part_id}</span>
                          {part.part_name && <span className="ml-2 text-text-muted">-- {part.part_name}</span>}
                          <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs text-blue-700">{part.schedule_type}</span>
                        </td>
                        {showNet && (
                          <td className="px-3 py-2 align-middle text-xs text-text-muted">
                            {part.stage_stock && part.stage_stock.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {part.stage_stock.map((s, si) => (
                                  <span key={si} className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-xs text-amber-700">
                                    {s.stage}: {s.qty}
                                  </span>
                                ))}
                              </div>
                            ) : <span className="text-text-muted">-</span>}
                          </td>
                        )}
                        <td className="px-3 py-2 align-middle text-right font-mono text-xs text-text-muted">{part.scheduled_qty.toLocaleString()} pcs</td>
                        <td className="px-3 py-2 align-middle text-right font-mono text-xs">
                          <span className="text-amber-600 font-semibold">{(part.pending_qty || 0).toLocaleString()}</span>
                          {(part.dispatched_qty || 0) > 0 && <span className="text-green-600 ml-1 text-[10px]">({part.dispatched_qty} done)</span>}
                        </td>
                        <td className="px-3 py-2 align-middle text-right font-mono text-xs text-text-muted">{part.required_weight.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                        {showNet && (
                          <>
                            <td className="px-3 py-2 align-middle text-right font-mono text-xs text-text-muted">-</td>
                            <td className="px-3 py-2 align-middle text-right font-mono text-xs text-green-600">{part.stage_stock_total || 0} pcs</td>
                            <td className="px-3 py-2 align-middle text-right font-mono text-xs text-purple-600">
                              {part.vendor_wip_total || 0} pcs
                              {part.vendor_wip && part.vendor_wip.length > 0 && (
                                <div className="flex flex-wrap gap-1 justify-end mt-0.5">
                                  {part.vendor_wip.map((w, wi) => (
                                    <span key={wi} className="inline-flex items-center rounded-full bg-purple-50 border border-purple-200 px-1.5 py-0.5 text-xs text-purple-600">
                                      {w.vendor_name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-middle text-right font-mono text-xs text-text-muted">-</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.length === 0 && !loading && (
        <div className="bg-white border border-border rounded-sm p-12 text-center">
          <p className="text-gray-700">Select a month and click CALCULATE to see RM requirements based on the production schedule.</p>
        </div>
      )}
    </div>
  );
}
